import { IDeckData } from "common/records/Deck";
import { IDoorData } from "common/records/Door";
import { IElevationData } from "common/records/Elevation";
import { IPoolData } from "common/records/Pool";
import { IRoofFaceData } from "common/records/RoofFace";
import { IRoomData } from "common/records/Room";
import { IWallFacadeData } from "common/records/WallFacade";
import { IWindowData } from "common/records/Window";
import { List, Map, Record } from "immutable";
import { DateStringNumber, Nullable } from "common/records";
import { geoJsonFromJSON } from "common/records/GeoJson";
import { imperial_uom, metric_uom } from "common/constants/uom";
import { checkDate } from "common/helpers/Date";
import { Parser } from "hot-formula-parser";
import round from "lodash/round";

/**
 * The `List<number>` id fields this factory derives from their nested payload arrays. Enumerated so
 * `measurementFromJSON` can normalize any that a caller supplied directly — see the loop there.
 */
const ID_LIST_KEYS = [
  "roof_face_ids",
  "wall_facade_ids",
  "window_ids",
  "door_ids",
  "room_ids",
  "elevation_ids",
  "deck_ids",
  "pool_ids",
] as const;

export const measurementFromJSON = (json: Partial<IMeasurementData>): MeasurementRecord => {
  const recordData: IMeasurementRecord = { ...(json as any) };

  if (json.roof_faces) {
    recordData.roof_face_ids = List(json.roof_faces.map((pog) => pog.id));
  }

  if (json.wall_facades) {
    recordData.wall_facade_ids = List(json.wall_facades.map((pog) => pog.id));
  }

  if (json.windows) {
    recordData.window_ids = List(json.windows.map((pog) => pog.id));
  }

  if (json.doors) {
    recordData.door_ids = List(json.doors.map((pog) => pog.id));
  }

  if (json.rooms) {
    recordData.room_ids = List(json.rooms.map((pog) => pog.id));
  }

  if (json.elevations) {
    recordData.elevation_ids = List(json.elevations.map((pog) => pog.id));
  }

  if (json.decks) {
    recordData.deck_ids = List(json.decks.map((pog) => pog.id));
  }

  if (json.pools) {
    recordData.pool_ids = List(json.pools.map((pog) => pog.id));
  }

  // Coerce any directly-set `*_ids` array to a `List`: this factory is the store-Immutable boundary.
  ID_LIST_KEYS.forEach((key) => {
    const ids = recordData[key] as unknown;
    if (Array.isArray(ids)) recordData[key] = List<number>(ids);
  });

  if (json.geojson) {
    recordData.geojson = geoJsonFromJSON(json.geojson);
  }

  if (json.locations) {
    recordData.locations = List(json.locations);
  }

  if (json.levels) {
    recordData.levels = List(json.levels);
  }

  if (json.created_at) {
    recordData.created_at = checkDate(json.created_at);
  }

  if (json.updated_at) {
    recordData.updated_at = checkDate(json.updated_at);
  }

  const record = new MeasurementRecord(recordData);

  return record;
};

export const lineMeasurementUpdate = List([
  "roof_ridge",
  "roof_hip",
  "roof_valley",
  "roof_eave",
  "roof_rake",
  "roof_step_flashing",
  "roof_headwall_flashing",
]);
export const propertiesMapping = List(["roof", "siding", "sunroom"]);

export const addMeasurementFormDefault = Map({
  name: "",
  location: "",
  level: "",
  height: 0,
  width: 0,
  quantity: 1,
  area: 0,
  ui: 0,
});

export const getUoM = (measurement: MeasurementRecord) => {
  if (!measurement || measurement.system === "imperial") {
    return imperial_uom;
  } else {
    return metric_uom;
  }
};

export const calculateFormula = (formula: Nullable<string>, measurement: MeasurementRecord) => {
  if (!formula) {
    return { result: 1, error: null };
  }

  try {
    const parser = setupFormulaParser(measurement);
    const { error } = parser.parse(formula);
    let { result } = parser.parse(formula);
    if (result) {
      result = round(parseFloat(result.toString()), 2);
    }
    return { result, error };
  } catch {
    return { result: null, error: "ERROR!" };
  }
};

export const setupFormulaParser = (measurement: MeasurementRecord) => {
  const parser = new Parser();
  columnKeys(measurement).forEach((k) => {
    parser.setVariable(k, measurement[k]);
  });
  return parser;
};

export const columnKeys = (
  measurement: MeasurementRecord,
  types: string[] = propertiesMapping.toArray(),
): (keyof Partial<IMeasurementRecord>)[] => {
  let keys: string[];
  if (Record.isRecord(measurement)) {
    keys = Object.keys(measurement.toJS());
  } else {
    keys = Object.keys(measurement);
  }

  const columnKeys: (keyof IMeasurementRecord)[] = [];
  keys.map((key) => {
    if (key === "roof_faces" || key === "roof_face_ids") return;

    const found = types.find((prop) => {
      return key.indexOf(prop) === 0;
    });
    if (found) {
      columnKeys.push(key as keyof IMeasurementRecord);
    }
  });

  return columnKeys;
};

export interface IMeasurementData {
  id: number;
  geojson: any;
  job_id: number;
  system: string;
  roof_1012_area: number;
  roof_10pw_sloped_area: number;
  roof_10pw_total_area: number;
  roof_12pw_total_area: number;
  roof_13p_area: number;
  roof_0_area: number;
  roof_1_area: number;
  roof_2_area: number;
  roof_3_area: number;
  roof_4_area: number;
  roof_5_area: number;
  roof_6_area: number;
  roof_7_area: number;
  roof_8_area: number;
  roof_9_area: number;
  roof_10_area: number;
  roof_11_area: number;
  roof_12_area: number;
  roof_13_area: number;
  roof_14_area: number;
  roof_15_area: number;
  roof_16_area: number;
  roof_17_area: number;
  roof_18_area: number;
  roof_19_area: number;
  roof_20_area: number;
  roof_21_area: number;
  roof_22_area: number;
  roof_23_area: number;
  roof_24_area: number;
  roof_25_area: number;
  roof_26p_area: number;
  roof_15pw_total_area: number;
  roof_20pw_total_area: number;
  roof_5pw_total_area: number;
  roof_79_area: number;
  roof_average_slope_area: number;
  roof_downspout_elbows: number;
  roof_downspouts: number;
  roof_eave: number;
  roof_flat_area: number;
  roof_flat_shallow_area: number;
  roof_gutter_miters: number;
  roof_headwall_flashing: number;
  roof_high_roof_area: number;
  roof_hip: number;
  roof_low_slope_area: number;
  roof_perimeter: number;
  roof_rake: number;
  roof_ridge: number;
  roof_ridge_hip: number;
  roof_sloped_area: number;
  roof_steep_slope_area: number;
  roof_step_flashing: number;
  roof_total_area: number;
  roof_total_flashing: number;
  roof_ultra_steep_slope_area: number;
  roof_valley: number;
  roof_valley_eave: number;
  roof_valley_eave_rake: number;
  siding_10pw_total_area: number;
  siding_15pw_total_area: number;
  siding_18pw_total_area: number;
  siding_5pw_total_area: number;
  siding_doors_qty: number;
  siding_frieze_board: number;
  siding_garage_doors_qty: number;
  siding_inside_corner_length: number;
  siding_inside_corner_qty: number;
  siding_level_frieze_board: number;
  siding_level_starter: number;
  siding_opening_qty: number;
  siding_opening_sides_length: number;
  siding_opening_sills_length: number;
  siding_opening_tops_length: number;
  siding_outside_corner_length: number;
  siding_outside_corner_qty: number;
  siding_shutter_area: number;
  siding_shutter_qty: number;
  siding_sloped_frieze_board: number;
  siding_sloped_trim: number;
  siding_soffit_eaves: number;
  siding_soffit_level_frieze: number;
  siding_soffit_rakes: number;
  siding_soffit_sloped_frieze: number;
  siding_soffit_total: number;
  siding_total_area: number;
  siding_vents_area: number;
  siding_vents_qty: number;
  siding_vertical_trim: number;
  siding_windows_qty: number;
  sunroom_a_wall_area: number;
  sunroom_a_wall_height: number;
  sunroom_a_wall_length: number;
  sunroom_b_wall_area: number;
  sunroom_b_wall_height: number;
  sunroom_b_wall_length: number;
  sunroom_c_wall_area: number;
  sunroom_c_wall_height: number;
  sunroom_c_wall_length: number;
  sunroom_cathedral_roof_area: number;
  sunroom_ceiling_area: number;
  sunroom_deck_total_area: number;
  sunroom_kw_a_area: number;
  sunroom_kw_a_height: number;
  sunroom_kw_a_length: number;
  sunroom_kw_b_area: number;
  sunroom_kw_b_height: number;
  sunroom_kw_b_length: number;
  sunroom_kw_c_area: number;
  sunroom_kw_c_height: number;
  sunroom_kw_c_length: number;
  sunroom_kw_total_area: number;
  sunroom_kw_total_length: number;
  sunroom_studio_roof_area: number;
  sunroom_wall_perimeter: number;
  sunroom_wall_total_area: number;
  url: string;

  roof_faces: IRoofFaceData[];
  wall_facades: IWallFacadeData[];
  windows: IWindowData[];
  doors: IDoorData[];
  rooms: IRoomData[];
  elevations?: IElevationData[];
  decks: IDeckData[];
  pools: IPoolData[];
  locations: string[];
  levels: string[];

  loading?: boolean;
  created_at: DateStringNumber;
  updated_at: DateStringNumber;
}

export interface IMeasurementRecord {
  id: number;
  geojson: any;
  job_id: number;
  system: string;
  roof_1012_area: number;
  roof_10pw_sloped_area: number;
  roof_10pw_total_area: number;
  roof_12pw_total_area: number;
  roof_13p_area: number;
  roof_0_area: number;
  roof_1_area: number;
  roof_2_area: number;
  roof_3_area: number;
  roof_4_area: number;
  roof_5_area: number;
  roof_6_area: number;
  roof_7_area: number;
  roof_8_area: number;
  roof_9_area: number;
  roof_10_area: number;
  roof_11_area: number;
  roof_12_area: number;
  roof_13_area: number;
  roof_14_area: number;
  roof_15_area: number;
  roof_16_area: number;
  roof_17_area: number;
  roof_18_area: number;
  roof_19_area: number;
  roof_20_area: number;
  roof_21_area: number;
  roof_22_area: number;
  roof_23_area: number;
  roof_24_area: number;
  roof_25_area: number;
  roof_26p_area: number;
  roof_15pw_total_area: number;
  roof_20pw_total_area: number;
  roof_5pw_total_area: number;
  roof_79_area: number;
  roof_average_slope_area: number;
  roof_downspout_elbows: number;
  roof_downspouts: number;
  roof_eave: number;
  roof_flat_area: number;
  roof_flat_shallow_area: number;
  roof_gutter_miters: number;
  roof_headwall_flashing: number;
  roof_high_roof_area: number;
  roof_hip: number;
  roof_low_slope_area: number;
  roof_perimeter: number;
  roof_rake: number;
  roof_ridge: number;
  roof_ridge_hip: number;
  roof_sloped_area: number;
  roof_steep_slope_area: number;
  roof_step_flashing: number;
  roof_total_area: number;
  roof_total_flashing: number;
  roof_ultra_steep_slope_area: number;
  roof_valley: number;
  roof_valley_eave: number;
  roof_valley_eave_rake: number;
  siding_10pw_total_area: number;
  siding_15pw_total_area: number;
  siding_18pw_total_area: number;
  siding_5pw_total_area: number;
  siding_doors_qty: number;
  siding_frieze_board: number;
  siding_garage_doors_qty: number;
  siding_inside_corner_length: number;
  siding_inside_corner_qty: number;
  siding_level_frieze_board: number;
  siding_level_starter: number;
  siding_opening_qty: number;
  siding_opening_sides_length: number;
  siding_opening_sills_length: number;
  siding_opening_tops_length: number;
  siding_outside_corner_length: number;
  siding_outside_corner_qty: number;
  siding_shutter_area: number;
  siding_shutter_qty: number;
  siding_sloped_frieze_board: number;
  siding_sloped_trim: number;
  siding_soffit_eaves: number;
  siding_soffit_level_frieze: number;
  siding_soffit_rakes: number;
  siding_soffit_sloped_frieze: number;
  siding_soffit_total: number;
  siding_total_area: number;
  siding_vents_area: number;
  siding_vents_qty: number;
  siding_vertical_trim: number;
  siding_windows_qty: number;
  sunroom_a_wall_area: number;
  sunroom_a_wall_height: number;
  sunroom_a_wall_length: number;
  sunroom_b_wall_area: number;
  sunroom_b_wall_height: number;
  sunroom_b_wall_length: number;
  sunroom_c_wall_area: number;
  sunroom_c_wall_height: number;
  sunroom_c_wall_length: number;
  sunroom_cathedral_roof_area: number;
  sunroom_ceiling_area: number;
  sunroom_deck_total_area: number;
  sunroom_kw_a_area: number;
  sunroom_kw_a_height: number;
  sunroom_kw_a_length: number;
  sunroom_kw_b_area: number;
  sunroom_kw_b_height: number;
  sunroom_kw_b_length: number;
  sunroom_kw_c_area: number;
  sunroom_kw_c_height: number;
  sunroom_kw_c_length: number;
  sunroom_kw_total_area: number;
  sunroom_kw_total_length: number;
  sunroom_studio_roof_area: number;
  sunroom_wall_perimeter: number;
  sunroom_wall_total_area: number;
  url: string;

  roof_face_ids: List<number>;
  wall_facade_ids: List<number>;
  window_ids: List<number>;
  door_ids: List<number>;
  room_ids: List<number>;
  elevation_ids: List<number>;
  deck_ids: List<number>;
  pool_ids: List<number>;
  locations: List<string>;
  levels: List<string>;

  _destroy: Nullable<boolean>;
  loading: boolean;
  created_at: Date;
  updated_at: Date;
}

export const defaultMeasurementProps: IMeasurementRecord = {
  id: 0,
  geojson: geoJsonFromJSON({}),
  job_id: 0,
  system: "imperial",
  roof_1012_area: 0,
  roof_10pw_sloped_area: 0,
  roof_10pw_total_area: 0,
  roof_12pw_total_area: 0,
  roof_13p_area: 0,
  roof_0_area: 0,
  roof_1_area: 0,
  roof_2_area: 0,
  roof_3_area: 0,
  roof_4_area: 0,
  roof_5_area: 0,
  roof_6_area: 0,
  roof_7_area: 0,
  roof_8_area: 0,
  roof_9_area: 0,
  roof_10_area: 0,
  roof_11_area: 0,
  roof_12_area: 0,
  roof_13_area: 0,
  roof_14_area: 0,
  roof_15_area: 0,
  roof_16_area: 0,
  roof_17_area: 0,
  roof_18_area: 0,
  roof_19_area: 0,
  roof_20_area: 0,
  roof_21_area: 0,
  roof_22_area: 0,
  roof_23_area: 0,
  roof_24_area: 0,
  roof_25_area: 0,
  roof_26p_area: 0,
  roof_15pw_total_area: 0,
  roof_20pw_total_area: 0,
  roof_5pw_total_area: 0,
  roof_79_area: 0,
  roof_average_slope_area: 0,
  roof_downspout_elbows: 0,
  roof_downspouts: 0,
  roof_eave: 0,
  roof_flat_area: 0,
  roof_flat_shallow_area: 0,
  roof_gutter_miters: 0,
  roof_headwall_flashing: 0,
  roof_high_roof_area: 0,
  roof_hip: 0,
  roof_low_slope_area: 0,
  roof_perimeter: 0,
  roof_rake: 0,
  roof_ridge: 0,
  roof_ridge_hip: 0,
  roof_sloped_area: 0,
  roof_steep_slope_area: 0,
  roof_step_flashing: 0,
  roof_total_area: 0,
  roof_total_flashing: 0,
  roof_ultra_steep_slope_area: 0,
  roof_valley: 0,
  roof_valley_eave: 0,
  roof_valley_eave_rake: 0,
  siding_10pw_total_area: 0,
  siding_15pw_total_area: 0,
  siding_18pw_total_area: 0,
  siding_5pw_total_area: 0,
  siding_doors_qty: 0,
  siding_frieze_board: 0,
  siding_garage_doors_qty: 0,
  siding_inside_corner_length: 0,
  siding_inside_corner_qty: 0,
  siding_level_frieze_board: 0,
  siding_level_starter: 0,
  siding_opening_qty: 0,
  siding_opening_sides_length: 0,
  siding_opening_sills_length: 0,
  siding_opening_tops_length: 0,
  siding_outside_corner_length: 0,
  siding_outside_corner_qty: 0,
  siding_shutter_area: 0,
  siding_shutter_qty: 0,
  siding_sloped_frieze_board: 0,
  siding_sloped_trim: 0,
  siding_soffit_eaves: 0,
  siding_soffit_level_frieze: 0,
  siding_soffit_rakes: 0,
  siding_soffit_sloped_frieze: 0,
  siding_soffit_total: 0,
  siding_total_area: 0,
  siding_vents_area: 0,
  siding_vents_qty: 0,
  siding_vertical_trim: 0,
  siding_windows_qty: 0,
  sunroom_a_wall_area: 0,
  sunroom_a_wall_height: 0,
  sunroom_a_wall_length: 0,
  sunroom_b_wall_area: 0,
  sunroom_b_wall_height: 0,
  sunroom_b_wall_length: 0,
  sunroom_c_wall_area: 0,
  sunroom_c_wall_height: 0,
  sunroom_c_wall_length: 0,
  sunroom_cathedral_roof_area: 0,
  sunroom_ceiling_area: 0,
  sunroom_deck_total_area: 0,
  sunroom_kw_a_area: 0,
  sunroom_kw_a_height: 0,
  sunroom_kw_a_length: 0,
  sunroom_kw_b_area: 0,
  sunroom_kw_b_height: 0,
  sunroom_kw_b_length: 0,
  sunroom_kw_c_area: 0,
  sunroom_kw_c_height: 0,
  sunroom_kw_c_length: 0,
  sunroom_kw_total_area: 0,
  sunroom_kw_total_length: 0,
  sunroom_studio_roof_area: 0,
  sunroom_wall_perimeter: 0,
  sunroom_wall_total_area: 0,
  url: "",

  roof_face_ids: List<number>(),
  wall_facade_ids: List<number>(),
  window_ids: List<number>(),
  door_ids: List<number>(),
  room_ids: List<number>(),
  elevation_ids: List<number>(),
  deck_ids: List<number>(),
  pool_ids: List<number>(),
  locations: List<string>(),
  levels: List<string>(),

  _destroy: null,
  loading: false,
  created_at: new Date(),
  updated_at: new Date(),
};

export class MeasurementRecord extends Record(defaultMeasurementProps) implements IMeasurementRecord {}
